/**
 * Iter 192 · Operations · Skip Analytics
 * Weekly dashboard of task-skip reasons. Highlights repeat-offender
 * customers so ops can act (correct address, cancel service, etc.).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { SkipForward, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import AppShell from "../../components/layout/AppShell";

const iso = (d) => d.toISOString().slice(0, 10);
const REASON_LABEL = {
  no_customer: "No customer / no response",
  gate_closed: "Gate closed",
  no_pickup: "Nothing to pick up",
  wrong_address: "Wrong address",
  later_service: "Later service",
  no_service_till: "Travelling / no service till date",
};

export default function SkipAnalytics() {
  const [dateFrom, setDateFrom] = useState(iso(new Date(Date.now() - 6 * 86400000)));
  const [dateTo, setDateTo]     = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/operations/skip-analytics?date_from=${dateFrom}&date_to=${dateTo}`,
      );
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const maxReason = useMemo(
    () => Math.max(1, ...(data?.reasons || []).map(r => r.count)),
    [data],
  );

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-testid="skip-analytics">
        <header className="flex items-center gap-2">
          <SkipForward size={22} weight="duotone" className="text-[#7A5A11]"/>
          <div>
            <h1 className="font-heading text-2xl">Skip analytics</h1>
            <p className="text-[12px] text-[#5C5A55]">
              Why field staff skipped tasks. Spot repeat customers early.
            </p>
          </div>
        </header>

        <div className="bg-white border border-[#E5E2D9] rounded-md p-3 flex flex-wrap items-end gap-2"
             data-testid="skip-analytics-filters">
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
            <div className="mb-1">From</div>
            <input type="date" value={dateFrom}
                   onChange={e => setDateFrom(e.target.value)}
                   data-testid="skip-analytics-from"
                   className="h-9 px-2 border border-[#E5E2D9] rounded text-[13px]"/>
          </label>
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
            <div className="mb-1">To</div>
            <input type="date" value={dateTo}
                   onChange={e => setDateTo(e.target.value)}
                   data-testid="skip-analytics-to"
                   className="h-9 px-2 border border-[#E5E2D9] rounded text-[13px]"/>
          </label>
          <div className="ml-auto text-[13px] text-[#1A1A1A] font-semibold"
                data-testid="skip-analytics-total">
            {data ? `${data.total_skips} skip${data.total_skips !== 1 ? "s" : ""} in window` : "…"}
          </div>
        </div>

        {loading && <div className="p-6 text-center italic text-[#8A8780]">Loading…</div>}
        {!loading && data && data.total_skips === 0 && (
          <div className="p-6 text-center italic text-[#8A8780] bg-white border border-[#E5E2D9] rounded-md">
            No skips recorded in this window.
          </div>
        )}

        {data && data.total_skips > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Reasons */}
              <div className="bg-white border border-[#E5E2D9] rounded-md p-3"
                   data-testid="skip-analytics-reasons">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8780] mb-2 font-semibold">
                  By reason
                </div>
                <div className="space-y-1.5">
                  {data.reasons.map(r => (
                    <div key={r.reason}
                          data-testid={`skip-reason-${r.reason}`}
                          className="flex items-center gap-2 text-[12px]">
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{REASON_LABEL[r.reason] || r.reason}</div>
                        <div className="h-1.5 rounded bg-[#F7F6F2] overflow-hidden mt-0.5">
                          <div className="h-full bg-[#7A5A11]"
                                style={{ width: `${(r.count / maxReason) * 100}%` }}/>
                        </div>
                      </div>
                      <span className="font-mono font-semibold w-8 text-right">{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top staff */}
              <div className="bg-white border border-[#E5E2D9] rounded-md p-3"
                   data-testid="skip-analytics-staff">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8780] mb-2 font-semibold">
                  Skips by field staff
                </div>
                <div className="space-y-1">
                  {data.top_staff.map(s => (
                    <div key={s.field_staff_id}
                          className="flex items-center justify-between text-[12px] border-b border-[#F0ECE3] py-1"
                          data-testid={`skip-staff-${s.field_staff_id}`}>
                      <span>{s.field_staff_name}</span>
                      <span className="font-mono font-semibold">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Repeat customers */}
            <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden"
                 data-testid="skip-analytics-repeat">
              <div className="px-3 py-2 border-b border-[#E5E2D9] bg-[#FFF7E6] flex items-center gap-2">
                <Warning size={14} weight="fill" className="text-[#7A5A11]"/>
                <div className="text-[13px] font-semibold text-[#7A5A11]">
                  Repeat-offender customers (≥ 2 skips in window)
                </div>
                <span className="ml-auto text-[11px] text-[#7A5A11]">
                  {data.repeat_customers.length} customer(s)
                </span>
              </div>
              {data.repeat_customers.length === 0 ? (
                <div className="p-6 italic text-[#8A8780] text-center">
                  Every skip in this window was a first for the customer.
                </div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.14em] text-[#5C5A55]">
                      <th className="text-left p-2">Customer</th>
                      <th className="text-right p-2">Skips</th>
                      <th className="text-left p-2">Reasons</th>
                      <th className="text-left p-2">Last skip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.repeat_customers.map(c => (
                      <tr key={c.customer_id}
                          data-testid={`skip-repeat-${c.customer_id}`}
                          className="border-t border-[#F0ECE3] hover:bg-[#F7F6F2]">
                        <td className="p-2 font-medium">{c.customer_name}</td>
                        <td className="p-2 text-right font-mono font-semibold">{c.count}</td>
                        <td className="p-2 text-[11px]">
                          {Object.entries(c.reasons).map(([r, n]) =>
                            `${REASON_LABEL[r] || r} × ${n}`).join(" · ")}
                        </td>
                        <td className="p-2 font-mono text-[11px]">
                          {c.last_skipped_at ? new Date(c.last_skipped_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
